<template>
  <div class="d-flex flex-column">
    <div class="d-flex flex-wrap">
      <v-select
        v-model="selectedType"
        :label="t('reports.typeLabel')"
        :items="[
          { title: t('reports.typeOptions.transactions'), value: 'transactions' },
          { title: t('reports.typeOptions.payouts'), value: 'payouts' },
          { title: t('reports.typeOptions.payoutDetail'), value: 'payout-detail' },
        ]"
        clearable
        density="comfortable"
        class="me-3"
        min-width="200"
      />
      <v-btn-nd class="ms-auto" width="50px" variant="text" @click="refresh">
        <v-icon>mdi-refresh</v-icon>
      </v-btn-nd>
    </div>
    <v-infinite-scroll :key="infiniteScrollKey" :items="reports" color="primary" @load="load">
      <template v-for="item in reports" :key="item.reportId">
        <v-card class="my-2 pa-3 d-flex align-center">
          <v-chip
            v-if="!xs"
            :color="item.state === 'READY' ? 'success' : 'default'"
            :variant="item.state === 'READY' ? 'tonal' : 'plain'"
            class="mr-2"
          >
            {{ t(`reports.state.${item.state}`) }}
          </v-chip>
          <div>
            <kbd>{{ item.fileName }}</kbd
            ><br />
            <span class="font-weight-thin font-italic text-caption">{{
              // @ts-expect-error: this is working correctly but TS doesn't know that
              t("reports.requestedAt", {
                date: d(new Date(item.requestedAt), "short", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                }),
              })
            }}</span>
          </div>
          <v-spacer />
          <div class="d-flex flex-column justify-center">
            <v-chip
              v-if="xs"
              :color="item.state === 'READY' ? 'success' : 'default'"
              :variant="item.state === 'READY' ? 'tonal' : 'plain'"
              class="mb-2"
            >
              {{ item.state }}
            </v-chip>
            <v-btn-nd
              color="secondary"
              size="default"
              :disabled="item.state !== 'READY'"
              :loading="!!downloading[item.reportId]"
              @click="download(item)"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn-nd>
          </div>
        </v-card>
      </template>
      <template #empty>
        <span v-if="(reports?.length ?? 0) > 0 && !lastKey">{{ t("reports.noMoreReports") }}</span>
        <span v-else-if="(reports?.length ?? -1) === 0">
          {{ t("reports.noReports") }}
          <router-link to="/transactions">{{ t("reports.noReportsText") }}</router-link>
        </span>
      </template>
    </v-infinite-scroll>
    <DownloadDialog v-model="reportDownloadDialog" :report-id="reportId" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import ReportApi, { Report } from "@/api/merchant-service/report";
import { API } from "@/plugins/api";

const { t, d } = useI18n();
const api = inject<API>("api");
const { xs } = useDisplay();
const route = useRoute();
const router = useRouter();

// VInfiniteScroll has internal state that needs to be reset when the type changes or during refresh
const infiniteScrollKey = ref(Math.random());
const reports = ref<Report[] | undefined>(undefined);
const lastKey = ref<string | undefined>(undefined);
const downloading = ref<{ [key: string]: boolean }>({});
const reportDownloadDialog = ref(false);
const reportId = ref<string | undefined>(undefined);
const selectedType = ref<string | undefined>(undefined);

onMounted(() => {
  if (route.params.reportId) {
    reportId.value = route.params.reportId as string;
    reportDownloadDialog.value = true;
  }
});
watch(reportDownloadDialog, (value: boolean, oldValue: boolean) => {
  if (oldValue && !value) {
    router.push({ name: "reports" }); // so that a refresh doesn't retrigger a download
  }
});
watch(selectedType, () => {
  reports.value = undefined;
  lastKey.value = undefined;
  infiniteScrollKey.value = Math.random();
});

const load = async (load: {
  side: string;
  done: (status: "loading" | "error" | "empty" | "ok") => void;
}) => {
  if (reports.value && !lastKey.value) {
    load.done("empty");
    return;
  }
  try {
    const response = await api!.report.listReports({
      type: selectedType.value?.toLocaleLowerCase() || undefined,
      lastKey: lastKey.value,
    });
    lastKey.value = response.lastKey;
    if (!reports.value) {
      reports.value = [];
    }
    reports.value.push(...response.data);
    load.done("ok");
  } catch (e) {
    console.error(e);
    load.done("error");
  }
};

const refresh = async () => {
  reports.value = undefined;
  lastKey.value = undefined;
  infiniteScrollKey.value = Math.random();
};

const download = async (report: Report) => {
  downloading.value[report.reportId] = true;
  try {
    const response = await api!.report.presignedUrl(report.reportId);
    await ReportApi.downloadReport(response.presignedUrl, response.fileName);
  } catch (e) {
    console.error(e);
  } finally {
    downloading.value[report.reportId] = false;
  }
};
</script>
